import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhotoIcon,
  CalendarDaysIcon,
  ArrowUpOnSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { ImagePlus } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

import { rtdb } from "../../../config/firebase";
import {
  ref,
  push,
  onValue,
  remove,
  off,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

export default function GaleriTraining() {
  const [photos, setPhotos] = useState([]);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [judul, setJudul] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [tanggal, setTanggal] = useState(new Date().toISOString().split("T")[0]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const storage = getStorage();
  const STORAGE_PATH = "GaleriTraining"; // Folder di Firebase Storage
  const NODE_NAME = "GaleriTraining"; // Node di Realtime Database

  // 🔹 Ambil data realtime dari Firebase
  useEffect(() => {
    setIsLoading(true);
    const galeriRef = ref(rtdb, NODE_NAME);

    const unsubscribe = onValue(
      galeriRef,
      (snapshot) => {
        const data = snapshot.val();
        const loaded = [];

        if (data) {
          for (const id in data) loaded.push({ id, ...data[id] });
          loaded.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        }

        setPhotos(loaded);
        setIsLoading(false);
      },
      (error) => {
        console.error("❌ Gagal memuat data:", error);
        toast.error("Gagal memuat galeri. Periksa koneksi internet.");
        setIsLoading(false);
      }
    );

    return () => off(galeriRef, "value", unsubscribe);
  }, []);

  // 🔹 Preview gambar sebelum upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 5MB!");
      return;
    }

    setNewImageFile(file);
    if (newImagePreview) URL.revokeObjectURL(newImagePreview);
    setNewImagePreview(URL.createObjectURL(file));
  };

  // 🔹 Upload ke Firebase Storage + simpan metadata ke Firebase RTDB
  const handleUpload = async () => {
    if (!judul || !keterangan || !tanggal || !newImageFile) {
      toast.error("Lengkapi semua field terlebih dahulu!");
      return;
    }

    setIsUploading(true);
    try {
      // 🔸 Nama file unik
      const fileExt = newImageFile.name.split(".").pop();
      const fileName = `training_${Date.now()}.${fileExt}`;
      const filePath = `${STORAGE_PATH}/${fileName}`;

      // 🔸 Upload file ke Firebase Storage
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, newImageFile);

      // 🔸 Ambil URL publik
      const fotoUrl = await getDownloadURL(fileRef);

      // 🔸 Simpan metadata ke Firebase Realtime Database
      await push(ref(rtdb, NODE_NAME), {
        judul,
        keterangan,
        tanggal,
        fotoUrl,
        filePath,
        createdAt: new Date().toISOString(),
      });

      // 🔸 Reset form
      setJudul("");
      setKeterangan("");
      setTanggal(new Date().toISOString().split("T")[0]);
      setNewImageFile(null);
      if (newImagePreview) URL.revokeObjectURL(newImagePreview);
      setNewImagePreview(null);

      toast.success("✅ Foto berhasil diunggah!");
    } catch (error) {
      console.error("❌ Upload error:", error);
      toast.error("Gagal mengunggah foto. Coba lagi!");
    } finally {
      setIsUploading(false);
    }
  };

  // 🔹 Hapus data + file dari Firebase
  const handleDelete = async (photo) => {
    if (!window.confirm(`Yakin ingin menghapus foto "${photo.judul}"?`)) return;

    try {
      // 🔸 Hapus file dari Storage
      if (photo.filePath) {
        const fileRef = storageRef(storage, photo.filePath);
        await deleteObject(fileRef).catch(() => {
          console.warn("⚠️ File tidak ditemukan di storage (mungkin sudah dihapus).");
        });
      }

      // 🔸 Hapus metadata dari Database
      await remove(ref(rtdb, `${NODE_NAME}/${photo.id}`));
      toast.success("🗑️ Foto berhasil dihapus!");
    } catch (error) {
      console.error("❌ Gagal hapus:", error);
      toast.error("Gagal menghapus foto. Coba lagi.");
    }
  };

  // 🔹 Loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <svg
          className="animate-spin h-8 w-8 text-green-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="ml-3 text-gray-700">Memuat galeri...</p>
      </div>
    );
  }

  // 🔹 UI utama
  return (
    <div className="relative min-h-screen bg-slate-50">
      <Toaster position="top-right" reverseOrder={false} />
      <main className="relative z-10 p-4 md:p-6">
        {/* Header */}
        <motion.header
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="sticky top-4 z-20 bg-white rounded-2xl shadow-md border border-gray-200 mb-8 p-5 flex items-center gap-3"
        >
          <PhotoIcon className="w-8 h-8 text-green-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Galeri <span className="text-green-600">Training</span>
            </h2>
            <p className="text-sm text-gray-500">
              Kelola foto-foto kegiatan pelatihan personel keamanan.
            </p>
          </div>
        </motion.header>

        {/* Upload Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <div className="p-6 rounded-2xl shadow-md border border-gray-200 bg-white mb-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {/* Upload Foto */}
            <div className="md:col-span-1">
              <label
                htmlFor="file-upload"
                className="cursor-pointer w-full aspect-square bg-transparent border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 hover:bg-gray-100 hover:border-green-500 transition"
              >
                {newImagePreview ? (
                  <img
                    src={newImagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <>
                    <ImagePlus className="w-12 h-12 text-green-600" />
                    <span className="mt-2 text-sm font-semibold text-gray-600">
                      Pilih Gambar
                    </span>
                  </>
                )}
              </label>
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Form Input */}
            <div className="md:col-span-2 space-y-4">
              <input
                type="text"
                placeholder="Judul Foto"
                value={judul}
                onChange={(e) => setJudul(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 text-sm shadow-sm focus:ring-2 focus:ring-green-500 bg-white"
              />
              <textarea
                placeholder="Keterangan Foto"
                value={keterangan}
                onChange={(e) => setKeterangan(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 text-sm shadow-sm focus:ring-2 focus:ring-green-500 bg-white"
                rows="3"
              />
              <div className="flex items-center gap-3">
                <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={tanggal}
                  onChange={(e) => setTanggal(e.target.value)}
                  className="border border-gray-300 rounded-md p-2 text-sm shadow-sm focus:ring-2 focus:ring-green-500 bg-white"
                />
              </div>

              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:scale-100"
              >
                {isUploading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Mengunggah...</span>
                  </>
                ) : (
                  <>
                    <ArrowUpOnSquareIcon className="w-5 h-5" />
                    <span>Upload Foto</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Galeri Foto */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Galeri Tersimpan
            </h3>
            <AnimatePresence mode="sync">
              {photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <motion.div
                      key={photo.id}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.3 }}
                      className="relative group aspect-square rounded-lg overflow-hidden shadow-md border border-gray-100 bg-white"
                    >
                      <a
                        href={photo.fotoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={photo.fotoUrl}
                          alt={photo.judul}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </a>
                      <div className="absolute bottom-0 left-0 p-3 bg-gradient-to-t from-black/60 via-black/20 to-transparent text-white">
                        <p className="font-bold text-sm">{photo.judul}</p>
                        <p className="text-xs text-gray-300 line-clamp-1">
                          {photo.keterangan}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(photo)}
                        className="absolute top-2 right-2 p-1.5 bg-white/30 text-gray-800 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:text-white transition-all"
                        title="Hapus Foto"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed rounded-lg text-gray-500">
                  <p>Belum ada foto di galeri.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
